import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { AppThemeSelector } from '@backstage/core-app-api';
import {
  AppTheme,
  AppThemeApi,
  appThemeApiRef,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  StorageApi,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createExtensionInput,
  createFrontendModule,
  PageLayout,
} from '@backstage/frontend-plugin-api';
import {
  SwappableComponentBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import useObservable from 'react-use/esm/useObservable';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { regaskDarkTheme, regaskLightTheme } from './regaskThemes';
import {
  APPEARANCE_BUCKET,
  getStoredThemeMode,
  setStoredThemeMode,
  subscribePaletteBackend,
  THEME_MODE_KEY,
  ThemeMode,
} from './palette';
import { subscribeFunModeBackend } from '../i18n/funMode';

/**
 * A single, stable theme Provider shared by both the light and dark
 * `ThemeBlueprint` entries. Backstage's `AppThemeProvider` renders
 * `appTheme.Provider` for the active theme; if light and dark each supplied a
 * distinct Provider closure, switching themes would change the component
 * *identity* at that node and React would unmount + remount the entire app
 * subtree (state lost, data refetched, scroll reset) — looking like a full
 * page reload. By pointing both themes at this one component, the identity is
 * stable across the switch: React keeps the tree mounted and MUI's
 * ThemeProvider just swaps the `theme` prop for a smooth, instant transition.
 */
/** Tracks the OS `prefers-color-scheme: dark` query, updating live on change. */
const usePrefersDark = (): boolean => {
  const query = useMemo(
    () => window.matchMedia('(prefers-color-scheme: dark)'),
    [],
  );
  const [prefersDark, setPrefersDark] = useState(query.matches);
  useEffect(() => {
    const listener = (event: MediaQueryListEvent) =>
      setPrefersDark(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, [query]);
  return prefersDark;
};

const RegaskThemeProvider = ({ children }: PropsWithChildren<{}>) => {
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );
  const prefersDark = usePrefersDark();
  // No explicit theme selected → "System": follow the OS preference live.
  const resolved = themeId ?? (prefersDark ? 'dark' : 'light');
  const theme = resolved === 'light' ? regaskLightTheme : regaskDarkTheme;
  return <UnifiedThemeProvider theme={theme} children={children} />;
};

/**
 * Regask light/dark themes. Registered with the names `light` and `dark` so
 * the resulting extension ids (`theme:app/light`, `theme:app/dark`) replace
 * the default Backstage themes provided by `@backstage/plugin-app`.
 */
const regaskLight = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Regask Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: RegaskThemeProvider,
    },
  },
});

const regaskDark = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Regask Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: RegaskThemeProvider,
    },
  },
});

/** Default theme mode for a brand-new user with nothing stored anywhere. */
const DEFAULT_THEME_MODE: ThemeMode = 'dark';

/**
 * AppThemeApi whose selection is persisted **per user** in the `appearance`
 * StorageApi bucket (server-side via the user-settings backend, with a
 * localStorage fallback) rather than the stock localStorage-only selector.
 *
 * The stored value is a {@link ThemeMode} (`light` | `dark` | `system`).
 * `system` maps to an undefined active theme id so `RegaskThemeProvider` /
 * Backstage's `AppThemeProvider` resolve it from `prefers-color-scheme` live.
 * A brand-new user (nothing stored) defaults to `dark`.
 */
class RegaskAppThemeApi implements AppThemeApi {
  private readonly selector: AppThemeSelector;
  private readonly bucket: StorageApi;

  constructor(themes: AppTheme[], storage: StorageApi) {
    this.selector = new AppThemeSelector(themes);
    this.bucket = storage.forBucket(APPEARANCE_BUCKET);

    // Pre-paint seed from the synchronous localStorage mirror (the StorageApi
    // snapshot is always 'unknown'), so there is no flash before the backend
    // value arrives.
    this.apply(getStoredThemeMode() ?? DEFAULT_THEME_MODE);

    // Reconcile with the server value on load + cross-device/tab changes.
    // Applies only (no write-back), so a local default never clobbers the
    // user's stored choice on another device.
    this.bucket.observe$<ThemeMode>(THEME_MODE_KEY).subscribe(s => {
      if (s.presence === 'present') this.apply(s.value);
    });
  }

  /** Drive the selector + refresh the local mirror, without writing the backend. */
  private apply(mode: ThemeMode): void {
    setStoredThemeMode(mode);
    this.selector.setActiveThemeId(mode === 'system' ? undefined : mode);
  }

  getInstalledThemes = (): AppTheme[] => this.selector.getInstalledThemes();
  activeThemeId$ = () => this.selector.activeThemeId$();
  getActiveThemeId = (): string | undefined => this.selector.getActiveThemeId();

  /** User-initiated change (undefined id = "System"): apply **and** persist. */
  setActiveThemeId = (themeId?: string): void => {
    const mode: ThemeMode = (themeId as ThemeMode | undefined) ?? 'system';
    this.apply(mode);
    void this.bucket.set<ThemeMode>(THEME_MODE_KEY, mode);
  };
}

/**
 * Replaces the stock `api:app/storage` (localStorage-only `WebStorage`) with
 * the user-settings `UserSettingsStorage`, so every StorageApi consumer —
 * including our appearance prefs (theme + palette), starred entities, etc. —
 * persists **per user** server-side via `@backstage/plugin-user-settings-backend`.
 * It falls back to localStorage automatically when signed out or the backend
 * is unavailable.
 */
const storageApi = ApiBlueprint.make({
  name: 'storage',
  params: defineParams =>
    defineParams({
      api: storageApiRef,
      deps: {
        fetchApi: fetchApiRef,
        discoveryApi: discoveryApiRef,
        errorApi: errorApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ fetchApi, discoveryApi, errorApi, identityApi }) =>
        UserSettingsStorage.create({
          namespace: 'regask',
          fetchApi,
          discoveryApi,
          errorApi,
          identityApi,
        }),
    }),
});

/**
 * Replaces the `api:app/app-theme` extension from `@backstage/plugin-app` with
 * {@link RegaskAppThemeApi}, which persists the theme mode per user (see above)
 * and supports a live "System" mode. Also reconciles the per-user palette from
 * the backend on load.
 *
 * The `themes` input mirrors the upstream extension so ThemeBlueprint
 * extensions (which attach to `api:app/app-theme` / `themes`) keep working.
 */
const appThemeApi = ApiBlueprint.makeWithOverrides({
  name: 'app-theme',
  inputs: {
    themes: createExtensionInput([ThemeBlueprint.dataRefs.theme], {
      replaces: [{ id: 'app', input: 'themes' }],
    }),
  },
  factory: (originalFactory, { inputs }) =>
    originalFactory(defineParams =>
      defineParams({
        api: appThemeApiRef,
        deps: { storage: storageApiRef },
        factory: ({ storage }) => {
          subscribePaletteBackend(storage);
          subscribeFunModeBackend(storage);
          return new RegaskAppThemeApi(
            inputs.themes.map(i => i.get(ThemeBlueprint.dataRefs.theme)),
            storage,
          );
        },
      }),
    ),
});

/**
 * Swaps the framework's `core.page-layout` (a hardcoded white header bar) for
 * the design-system RegaskPageLayout, so stock plugin pages (Settings et al.)
 * get the same PageHeader + tab styling as our own pages.
 */
const regaskPageLayout = SwappableComponentBlueprint.make({
  // Same name + plugin (`app`) as the stock swap (component:app/core-page-layout)
  // so this replaces it rather than registering a second component for the ref.
  name: 'core-page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => import('./RegaskPageLayout').then(m => m.RegaskPageLayout),
    }),
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    regaskLight,
    regaskDark,
    storageApi,
    appThemeApi,
    regaskPageLayout,
  ],
});
